/** La ficha de una solicitud: todo lo que se sabe, y lo que se puede hacer con ella.
 *
 * Los botones salen de `estados_posibles` y de `puedo_*`, que los calcula el servidor con las
 * mismas reglas que luego aplica. No hay ningún control aquí que pueda acabar en un error de
 * permisos.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import {
  Cargando,
  Dialogo,
  Error,
  EtiquetaDeEstado,
  EtiquetaDePrioridad,
  Vacio,
} from '../componentes/piezas'
import {
  BotonDeAdjuntar, ListaDeAdjuntos, PendientesDeSubir, manejadoresDeArchivos, useSubidas,
} from '../componentes/adjuntos'
import { CajaDeComentario, TextoConMenciones } from '../componentes/menciones'
import { usePortal } from '../contexto'
import { ESTADO_SOLICITUD, PRIORIDAD, fecha, fechaYHora, hace } from '../textos'
import { accionDe, type Accion } from './acciones'
import type {
  Adjunto,
  Comentario,
  EstadoSolicitud,
  LineaDeActividad,
  Miembro,
  Persona,
  Prioridad,
  Proyecto,
  Solicitud as Datos,
} from '../tipos'

export default function Solicitud() {
  const { numero = '' } = useParams()
  const { ruta, servidor, departamentos, avisar } = usePortal()
  const navegar = useNavigate()

  const [datos, setDatos] = useState<Datos | null>(null)
  const [comentarios, setComentarios] = useState<Comentario[]>([])
  const [historial, setHistorial] = useState<LineaDeActividad[]>([])
  const [error, setError] = useState('')
  const [dialogo, setDialogo] = useState<'aceptar' | 'rechazar' | 'convertir' | 'responsable' | null>(null)
  // Un paso que pide (u ofrece) una nota antes de confirmarse, p. ej. «Enviar a cierre».
  const [paso, setPaso] = useState<{ estado: EstadoSolicitud; accion: Accion } | null>(null)

  const cargar = useCallback(async () => {
    try {
      const ficha = await api.get<Datos>(`${ruta}/solicitudes/${numero}`)
      setDatos(ficha)
      const [c, h] = await Promise.all([
        api.get<Comentario[]>(`${ruta}/solicitudes/${numero}/comentarios`),
        api.get<LineaDeActividad[]>(`${ruta}/solicitudes/${numero}/historial`),
      ])
      setComentarios(c)
      setHistorial(h)
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta, numero])

  useEffect(() => {
    setDatos(null)
    void cargar()
  }, [cargar])

  async function mover(estado: EstadoSolicitud, nota = '') {
    try {
      setDatos(await api.post<Datos>(`${ruta}/solicitudes/${numero}/estado`, { estado, nota }))
      avisar(`Ahora está ${ESTADO_SOLICITUD[estado].toLowerCase()}.`, 'bien')
      setPaso(null)
      void cargar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  const base = `/${servidor.id}`
  const departamento = departamentos.find((d) => d.id === datos?.departamento_id)

  if (error) {
    return (
      <div className="contenido">
        <Error>{error}</Error>
        <Link className="boton" to={`${base}/solicitudes`}>Volver a la bandeja</Link>
      </div>
    )
  }
  if (!datos) return <div className="contenido"><Cargando filas={6} /></div>

  // `rejected` tiene su propio diálogo porque exige un motivo; el resto van directos.
  const sueltos = datos.estados_posibles.filter((e) => e !== 'rejected' && e !== 'approved')

  return (
    <>
      <header className="cabecera">
        <Link className="boton boton--plano" to={`${base}/solicitudes`} aria-label="Volver">←</Link>
        <div>
          <div className="codigo">{datos.codigo}</div>
          <h1>{datos.titulo}</h1>
        </div>
        <div className="cabecera__acciones">
          <EtiquetaDeEstado texto={datos.estado_texto} />
        </div>
      </header>

      <div className="contenido">
        <div className="detalle">
          <div>
            {(datos.puedo_decidir || datos.estados_posibles.length > 0) && (
              <div className="tarjeta" style={{ marginBottom: 16 }}>
                <div className="tarjeta__titulo">
                  {datos.puedo_decidir ? 'Qué se hace con esto' : 'Qué puedes hacer'}
                </div>
                <div className="tarjeta__cuerpo">
                  {datos.estado === 'closing' && (
                    <p className="aviso-paso">
                      {datos.puedo_decidir
                        ? 'Desarrollo la ha enviado a cierre. Comprueba que está bien: ciérrala o devuélvela diciendo qué falta.'
                        : 'Enviada a cierre: espera la comprobación de administración.'}
                    </p>
                  )}
                  <div className="fila-botones">
                    {datos.puedo_decidir && datos.estados_posibles.includes('approved') && (
                      <button className="boton boton--principal" onClick={() => setDialogo('aceptar')}>
                        Aceptar
                      </button>
                    )}
                    {datos.puedo_decidir && datos.estados_posibles.includes('rejected') && (
                      <button className="boton boton--malo" onClick={() => setDialogo('rechazar')}>
                        Rechazar
                      </button>
                    )}
                    {sueltos.map((estado) => {
                      const accion = accionDe(datos.estado, estado, datos.puedo_decidir)
                      return (
                        <button
                          key={estado}
                          className={`boton${accion.estilo === 'principal' ? ' boton--principal' : accion.estilo === 'malo' ? ' boton--malo' : ''}`}
                          onClick={() => (accion.nota === 'no' ? void mover(estado) : setPaso({ estado, accion }))}
                        >
                          {accion.texto}
                        </button>
                      )
                    })}
                    {datos.puedo_decidir && !datos.proyecto &&
                      !['new', 'reviewing', 'needs_info', 'rejected', 'cancelled', 'completed', 'closing']
                        .includes(datos.estado) && (
                        <button className="boton" onClick={() => setDialogo('convertir')}>
                          Convertir en proyecto
                        </button>
                      )}
                  </div>
                </div>
              </div>
            )}

            {datos.nota_de_decision && (
              <div className="tarjeta" style={{ marginBottom: 16 }}>
                <div className="tarjeta__titulo">Respuesta de administración</div>
                <div className="tarjeta__cuerpo">
                  <p className="texto-largo" style={{ margin: 0 }}>{datos.nota_de_decision}</p>
                  {datos.decidida && (
                    <div className="fila__meta" style={{ marginTop: 6 }}>
                      {fechaYHora(datos.decidida)}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="tarjeta">
              <div className="tarjeta__cuerpo">
                <Texto titulo="Descripción" valor={datos.descripcion} />
                <Texto titulo="Por qué hace falta" valor={datos.justificacion} />
                <Texto titulo="A quién afecta" valor={datos.impacto} />
              </div>
            </div>

            <AdjuntosDeSolicitud datos={datos} numero={numero} alCambiar={cargar} />

            <section className="tarjeta" style={{ marginTop: 16 }}>
              <div className="tarjeta__titulo">
                Comentarios {comentarios.length > 0 && `(${comentarios.length})`}
              </div>
              {comentarios.length === 0 ? (
                <Vacio titulo="Sin comentarios todavía" />
              ) : (
                comentarios.map((c) => (
                  <div key={c.id} className={`comentario${c.interno ? ' comentario--interno' : ''}`}>
                    <div className="comentario__cabecera">
                      <span className="comentario__autor">{c.autor?.nombre ?? 'Alguien'}</span>
                      <span className="comentario__cuando">{hace(c.cuando)}</span>
                      {c.interno && <span className="etiqueta">Interno</span>}
                    </div>
                    {c.cuerpo && <TextoConMenciones cuerpo={c.cuerpo} menciones={c.menciones} />}
                    <ListaDeAdjuntos adjuntos={c.adjuntos} />
                  </div>
                ))
              )}
              <Comentar
                numero={numero}
                puedeInterno={datos.puedo_comentar_en_interno}
                alEnviar={cargar}
              />
            </section>
          </div>

          <aside>
            <div className="tarjeta">
              <div className="tarjeta__titulo">Datos</div>
              <div className="tarjeta__cuerpo" style={{ paddingTop: 4, paddingBottom: 4 }}>
                <Dato nombre="Solicita" valor={datos.solicitante?.nombre ?? '—'} />
                <Dato nombre="Departamento" valor={departamento?.nombre ?? '—'} />
                <Dato nombre="Enviada" valor={fecha(datos.creada)} />
                <Dato nombre="Pedida como" valor={PRIORIDAD[datos.prioridad_pedida]} />
                <div className="dato">
                  <span className="dato__nombre">Prioridad</span>
                  <span className="dato__valor"><EtiquetaDePrioridad valor={datos.prioridad} /></span>
                </div>
                <div className="dato">
                  <span className="dato__nombre">Responsable</span>
                  <span className="dato__valor">
                    {datos.responsable?.nombre ?? 'Sin asignar'}
                    {datos.puedo_cambiar_responsable && (
                      <>
                        {' '}
                        <button
                          className="boton boton--plano"
                          style={{ padding: '0 6px', minHeight: 0 }}
                          onClick={() => setDialogo('responsable')}
                        >
                          Cambiar
                        </button>
                      </>
                    )}
                  </span>
                </div>
                <Dato
                  nombre="Estimación"
                  valor={datos.estimada ? fecha(datos.estimada) : 'Sin estimar'}
                />
                <Dato
                  nombre="Fecha comprometida"
                  valor={datos.comprometida ? fecha(datos.comprometida) : 'Ninguna'}
                />
                {datos.horas_estimadas != null && (
                  <Dato nombre="Horas estimadas" valor={`${datos.horas_estimadas} h`} />
                )}
                {datos.proyecto && (
                  <Dato nombre="Proyecto" valor={datos.proyecto} />
                )}
              </div>
              {(datos.estimada || datos.comprometida) && (
                <div className="tarjeta__cuerpo" style={{ paddingTop: 0 }}>
                  <div className="campo__ayuda">
                    {datos.comprometida
                      ? 'La fecha comprometida es un compromiso; la estimación, no.'
                      : 'Es una estimación, no una fecha comprometida: puede moverse.'}
                  </div>
                </div>
              )}
            </div>

            <div className="tarjeta" style={{ marginTop: 14 }}>
              <div className="tarjeta__titulo">Historial</div>
              <div className="tarjeta__cuerpo">
                {historial.length === 0 ? (
                  <span style={{ color: 'var(--texto-tenue)' }}>Nada todavía.</span>
                ) : (
                  <ul className="historial">
                    {historial.map((l) => (
                      <li key={l.id}>
                        {l.frase}
                        <time dateTime={l.cuando}>{fechaYHora(l.cuando)}</time>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {paso && (
        <PasoConNota
          paso={paso}
          alCerrar={() => setPaso(null)}
          alConfirmar={(nota) => mover(paso.estado, nota)}
        />
      )}

      {dialogo === 'aceptar' && (
        <Aceptar
          ruta={`${ruta}/solicitudes/${numero}`}
          alCerrar={() => setDialogo(null)}
          alHecho={() => {
            setDialogo(null)
            avisar('Solicitud aceptada.', 'bien')
            void cargar()
          }}
        />
      )}

      {dialogo === 'responsable' && (
        <CambiarResponsable
          ruta={`${ruta}/solicitudes/${numero}`}
          actual={datos.responsable}
          alCerrar={() => setDialogo(null)}
          alHecho={(ficha) => {
            setDialogo(null)
            setDatos(ficha)
            avisar(ficha.responsable ? `Ahora la lleva ${ficha.responsable.nombre}.` : 'Sin responsable.', 'bien')
            void cargar()
          }}
        />
      )}

      {dialogo === 'rechazar' && (
        <Rechazar
          alCerrar={() => setDialogo(null)}
          alRechazar={async (motivo) => {
            setDialogo(null)
            await mover('rejected', motivo)
          }}
        />
      )}

      {dialogo === 'convertir' && (
        <Convertir
          ruta={`${ruta}/solicitudes/${numero}/proyecto`}
          alCerrar={() => setDialogo(null)}
          alHecho={(codigo, proyectoNumero) => {
            setDialogo(null)
            avisar(`Creado ${codigo}.`, 'bien')
            navegar(`${base}/proyectos/${proyectoNumero}`)
          }}
        />
      )}
    </>
  )
}

function CambiarResponsable({
  ruta,
  actual,
  alCerrar,
  alHecho,
}: {
  ruta: string
  actual: Persona | null
  alCerrar: () => void
  alHecho: (ficha: Datos) => void
}) {
  const [gente, setGente] = useState<Persona[] | null>(null)
  const [elegido, setElegido] = useState(actual?.id ?? '')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.get<Persona[]>(`${ruta}/responsables`)
      .then(setGente)
      .catch((e) => { setGente([]); setError((e as ErrorDeApi).message) })
  }, [ruta])

  async function guardar() {
    setEnviando(true)
    setError('')
    try {
      alHecho(await api.put<Datos>(`${ruta}/responsable`, { responsable_id: elegido || null }))
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Persona encargada"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button
            className="boton boton--principal"
            onClick={guardar}
            disabled={enviando || gente === null || elegido === (actual?.id ?? '')}
          >
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}
      <div className="campo">
        <label htmlFor="quien">Quién la lleva</label>
        <select id="quien" value={elegido} onChange={(e) => setElegido(e.target.value)}>
          <option value="">Sin responsable</option>
          {(gente ?? []).map((p) => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>
        <div className="campo__ayuda">Sólo administración y desarrollo pueden llevar solicitudes.</div>
      </div>
    </Dialogo>
  )
}

function Texto({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="bloque">
      <div className="bloque__titulo">{titulo}</div>
      {valor.trim() ? (
        <div className="texto-largo">{valor}</div>
      ) : (
        <span style={{ color: 'var(--texto-tenue)' }}>No se rellenó.</span>
      )}
    </div>
  )
}

function Dato({ nombre, valor }: { nombre: string; valor: string }) {
  return (
    <div className="dato">
      <span className="dato__nombre">{nombre}</span>
      <span className="dato__valor">{valor}</span>
    </div>
  )
}

function Comentar({
  numero,
  puedeInterno,
  alEnviar,
}: {
  numero: string
  puedeInterno: boolean
  alEnviar: () => Promise<void>
}) {
  const { ruta } = usePortal()
  return (
    <div className="tarjeta__cuerpo" style={{ borderTop: '1px solid var(--borde)' }}>
      <CajaDeComentario
        rutaArchivos={`${ruta}/archivos`}
        camposDeSubida={{ solicitud: numero }}
        cargarCandidatos={(interno) =>
          api.get<Persona[]>(`${ruta}/solicitudes/${numero}/mencionables?interno=${interno}`)}
        puedeInterno={puedeInterno}
        alEnviar={async (datos) => {
          await api.post(`${ruta}/solicitudes/${numero}/comentarios`, datos)
          await alEnviar()
        }}
      />
    </div>
  )
}

/** Las fotos y archivos de la propia solicitud (los de cada comentario van en el suyo). */
function AdjuntosDeSolicitud({
  datos,
  numero,
  alCambiar,
}: {
  datos: Datos
  numero: string
  alCambiar: () => Promise<void>
}) {
  const { ruta, avisar } = usePortal()
  const puede = datos.puedo_editar || datos.puedo_decidir
  const subidas = useSubidas(`${ruta}/archivos`, { solicitud: numero })
  const [ocupado, setOcupado] = useState(false)
  const arrastre = manejadoresDeArchivos(subidas)

  if (datos.adjuntos.length === 0 && !puede) return null

  async function anadir() {
    setOcupado(true)
    try {
      await api.post(`${ruta}/solicitudes/${numero}/adjuntos`, { adjuntos: subidas.tokens })
      subidas.limpiar()
      await alCambiar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    } finally {
      setOcupado(false)
    }
  }

  async function quitar(a: Adjunto) {
    try {
      await api.borrar(`${ruta}/solicitudes/${numero}/adjuntos/${a.token}`)
      await alCambiar()
    } catch (e) {
      avisar((e as ErrorDeApi).message, 'malo')
    }
  }

  return (
    <section className="tarjeta" style={{ marginTop: 16 }} {...(puede ? arrastre : {})}>
      <div className="tarjeta__titulo">
        Fotos y archivos {datos.adjuntos.length > 0 && `(${datos.adjuntos.length})`}
      </div>
      <div className="tarjeta__cuerpo">
        {datos.adjuntos.length === 0 ? (
          <span style={{ color: 'var(--texto-tenue)' }}>Todavía no hay ninguno.</span>
        ) : (
          <ListaDeAdjuntos adjuntos={datos.adjuntos} alQuitar={puede ? quitar : undefined} />
        )}
        {puede && (
          <div style={{ marginTop: 10 }}>
            <PendientesDeSubir subidas={subidas} />
            <div className="fila-botones" style={{ alignItems: 'center' }}>
              <BotonDeAdjuntar subidas={subidas} texto="Añadir fotos o archivos" />
              {subidas.tokens.length > 0 && (
                <button className="boton boton--principal" disabled={ocupado || subidas.subiendo}
                        onClick={() => void anadir()}>
                  {ocupado ? 'Añadiendo…' : `Añadir a la solicitud (${subidas.tokens.length})`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/** Confirmar un paso que lleva nota: «Enviar a cierre», «Devolver a desarrollo»… */
function PasoConNota({
  paso,
  alCerrar,
  alConfirmar,
}: {
  paso: { estado: EstadoSolicitud; accion: Accion }
  alCerrar: () => void
  alConfirmar: (nota: string) => Promise<void>
}) {
  const [nota, setNota] = useState('')
  const [enviando, setEnviando] = useState(false)
  const obligatoria = paso.accion.nota === 'obligatoria'

  async function confirmar() {
    setEnviando(true)
    try {
      await alConfirmar(nota.trim())
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo={paso.accion.texto}
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button
            className={`boton ${paso.accion.estilo === 'malo' ? 'boton--malo' : 'boton--principal'}`}
            onClick={confirmar}
            disabled={enviando || (obligatoria && !nota.trim())}
          >
            {enviando ? 'Guardando…' : 'Confirmar'}
          </button>
        </>
      }
    >
      {paso.accion.ayuda && <p style={{ marginTop: 0 }}>{paso.accion.ayuda}</p>}
      <div className="campo">
        <label htmlFor="nota-paso">{paso.accion.pregunta ?? 'Nota'}</label>
        <textarea
          id="nota-paso" autoFocus value={nota} maxLength={2000}
          onChange={(e) => setNota(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && (!obligatoria || nota.trim())) void confirmar()
          }}
        />
      </div>
    </Dialogo>
  )
}

function Aceptar({
  ruta,
  alCerrar,
  alHecho,
}: {
  ruta: string
  alCerrar: () => void
  alHecho: () => void
}) {
  const { ruta: base } = usePortal()
  const [prioridad, setPrioridad] = useState<Prioridad>('medium')
  const [responsable, setResponsable] = useState('')
  const [estimada, setEstimada] = useState('')
  const [comprometida, setComprometida] = useState('')
  const [horas, setHoras] = useState('')
  const [nota, setNota] = useState('')
  const [gente, setGente] = useState<Miembro[]>([])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.get<Miembro[]>(`${base}/equipo`).then(setGente).catch(() => setGente([]))
  }, [base])

  async function enviar() {
    setEnviando(true)
    setError('')
    try {
      await api.post(`${ruta}/aceptar`, {
        prioridad,
        responsable_id: responsable || null,
        estimada: estimada || null,
        comprometida: comprometida || null,
        horas: horas ? Number(horas) : null,
        nota,
      })
      alHecho()
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Aceptar la solicitud"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando}>
            {enviando ? 'Guardando…' : 'Aceptar'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}

      <div className="campo">
        <label htmlFor="prio">Prioridad</label>
        <select id="prio" value={prioridad} onChange={(e) => setPrioridad(e.target.value as Prioridad)}>
          {Object.entries(PRIORIDAD).map(([clave, texto]) => (
            <option key={clave} value={clave}>{texto}</option>
          ))}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="resp">Responsable</label>
        <select id="resp" value={responsable} onChange={(e) => setResponsable(e.target.value)}>
          <option value="">Sin asignar de momento</option>
          {gente.map((m) => (
            <option key={m.id} value={m.persona.id}>{m.persona.nombre}</option>
          ))}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="est">Estimación</label>
        <input id="est" type="date" value={estimada} onChange={(e) => setEstimada(e.target.value)} />
        <div className="campo__ayuda">
          Una fecha aproximada. Se puede mover sin dar explicaciones.
        </div>
      </div>

      <div className="campo">
        <label htmlFor="comp">Fecha comprometida</label>
        <input id="comp" type="date" value={comprometida}
               onChange={(e) => setComprometida(e.target.value)} />
        <div className="campo__ayuda">
          <strong>Esto sí es una promesa.</strong> Déjala vacía si aún no hay compromiso.
        </div>
      </div>

      <div className="campo">
        <label htmlFor="horas">Horas estimadas (opcional)</label>
        <input id="horas" type="number" min={0} value={horas}
               onChange={(e) => setHoras(e.target.value)} />
      </div>

      <div className="campo">
        <label htmlFor="nota">Comentario para quien lo pidió</label>
        <textarea id="nota" value={nota} onChange={(e) => setNota(e.target.value)}
                  placeholder="Adelante, pero lo miramos después del despliegue." />
      </div>
    </Dialogo>
  )
}

function Rechazar({
  alCerrar,
  alRechazar,
}: {
  alCerrar: () => void
  alRechazar: (motivo: string) => Promise<void>
}) {
  const [motivo, setMotivo] = useState('')

  return (
    <Dialogo
      titulo="Rechazar la solicitud"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar}>Cancelar</button>
          <button
            className="boton boton--malo"
            disabled={!motivo.trim()}
            onClick={() => void alRechazar(motivo)}
          >
            Rechazar
          </button>
        </>
      }
    >
      <div className="campo">
        <label htmlFor="motivo">Por qué se rechaza</label>
        <textarea
          id="motivo" value={motivo} autoFocus
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ya lo cubre el sistema de informes que hay. Si falta algo concreto, abrid otra."
        />
        <div className="campo__ayuda">
          Hace falta. Quien lo pidió va a leer esto, y «no» a secas no le sirve de nada.
        </div>
      </div>
    </Dialogo>
  )
}

const SUGERIDAS = ['Diseño de la base', 'Backend', 'Frontend', 'Integración', 'Pruebas', 'Despliegue']

function Convertir({
  ruta,
  alCerrar,
  alHecho,
}: {
  ruta: string
  alCerrar: () => void
  alHecho: (codigo: string, numero: number) => void
}) {
  const [tareas, setTareas] = useState(SUGERIDAS.join('\n'))
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function enviar() {
    setEnviando(true)
    setError('')
    try {
      const proyecto = await api.post<Proyecto>(ruta, {
        tareas: tareas.split('\n').map((t) => t.trim()).filter(Boolean),
      })
      alHecho(proyecto.codigo, proyecto.numero)
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Convertir en proyecto"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear el proyecto'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}
      <p>
        El proyecto hereda el título, la descripción, la prioridad, el responsable y las fechas
        de la solicitud. No hay que copiar nada.
      </p>
      <div className="campo">
        <label htmlFor="tareas">Tareas iniciales, una por línea</label>
        <textarea id="tareas" value={tareas} style={{ minHeight: 150 }}
                  onChange={(e) => setTareas(e.target.value)} />
        <div className="campo__ayuda">Son una sugerencia. Quita las que no apliquen.</div>
      </div>
    </Dialogo>
  )
}
